import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { CloseAction } from '@components/Actions/CloseAction/CloseAction';

import { Text } from '@components/Text/Text';
import { Link } from '@components/Link/Link';
import { Icon } from '@components/Icon/Icon';

import type { CardHeaderProps } from './CardHeader';

export const Wrap = styled.div`
  position: relative;
  ${tokens.elevation(2)};
  background: ${tokens.color.ui_background};
  border-radius: ${tokens.radius.l};
`;

const HEADER_HEIGHT = {
  m: tokens.spacer.x8,
  l: '84px',
};

export const Header = styled.header<{ variant: CardHeaderProps['variant'] }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ variant }) => HEADER_HEIGHT[variant]};
  padding: 0 ${tokens.spacer.x2};
  border-bottom: 1px solid ${tokens.color.ui_border_light};
`;

export const HeaderChildrenContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`;

export const CloseButton = styled(CloseAction)`
  z-index: 2;
  position: absolute;
  margin: ${tokens.spacer.x0};
  top: ${tokens.spacer.x2};
  right: ${tokens.spacer.x2};

  /* CloseButton is in the Card, Header in the CardHeader. Since the 2 must visually interact, we use advanced css */
  & ~ ${Header} {
    padding-right: calc(${tokens.spacer.x7});

    /* We show a separator only if there is a children in CardHeader */
    ${HeaderChildrenContainer}::after {
      content: '';
      position: absolute;
      top: 0px;
      bottom: 0px;
      width: 0px;
      margin: auto;
      height: ${tokens.spacer.x3};
      right: -${tokens.spacer.x0};
      border-right: 1px solid ${tokens.color.ui_border_light};
    }
  }
`;

export const HeaderTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-right: ${tokens.spacer.x1};
`;

const HEADER_TITLE_TEXT = {
  m: tokens.text('subtitle1'),
  l: tokens.text('header5'),
};

const HEADER_TITLE_MARGIN_BOTTOM = {
  m: 0,
  l: tokens.spacer.x0pt5,
};

export const StyledLink = styled(Link)<{ variant: CardHeaderProps['variant'] }>`
  color: ${tokens.color.text_default};
  text-align: left;
  ${({ variant }) => HEADER_TITLE_TEXT[variant]};
  margin-bottom: ${({ variant }) => HEADER_TITLE_MARGIN_BOTTOM[variant]};
  ${tokens.transition('color')};
  text-decoration: none;

  :hover {
    color: ${tokens.color.link_alt};
  }
`;

export const HeaderText = styled(Text)<{
  capitalized: boolean;
  variant: CardHeaderProps['variant'];
}>`
  margin: 0;
  text-transform: ${({ capitalized }) => (capitalized ? 'capitalize' : 'none')};
  ${({ variant }) => HEADER_TITLE_TEXT[variant]};
  ${({ variant }) => HEADER_TITLE_MARGIN_BOTTOM[variant]};
`;

const HEADER_SUBTITLE_TEXT = {
  m: tokens.text('caption'),
  l: tokens.text('body2'),
};

export const HeaderSubtitle = styled(HeaderText)<{
  variant: CardHeaderProps['variant'];
}>`
  ${({ variant }) => HEADER_SUBTITLE_TEXT[variant]};
`;

export const Content = styled.div`
  padding: ${tokens.spacer.x3};
`;

export const StyledIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x2};
  color: ${tokens.color.primary_cta};
`;
